import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity as ActivityIcon, Brain, FileText, RefreshCw, Sparkles, User, Mail, Zap, KanbanSquare, Filter, X } from "lucide-react";

const ICONS = {
  ran_bid_analysis: Brain,
  drafted_proposal: FileText,
  saved_opportunity: Sparkles,
  refreshed_sam: RefreshCw,
  moved_stage: KanbanSquare,
  invited_member: User,
  sent_email: Mail,
  autonomous_run: Zap,
};

const LABELS = {
  ran_bid_analysis: "ran Bid/No-Bid analysis on",
  drafted_proposal: "drafted a proposal for",
  saved_opportunity: "saved opportunity",
  refreshed_sam: "refreshed SAM.gov feed",
  moved_stage: "moved to",
  invited_member: "invited",
  sent_email: "sent deadline email to",
  autonomous_run: "ran the autonomous agent",
  uploaded_document: "uploaded",
  updated_branding: "updated branding",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ActivityFeed() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ actors: [], actions: [] });
  const [loading, setLoading] = useState(false);
  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("");
  const [opportunityId, setOpportunityId] = useState("");
  const [sinceDays, setSinceDays] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actorId) params.set("actor_id", actorId);
      if (action) params.set("action", action);
      if (opportunityId.trim()) params.set("opportunity_id", opportunityId.trim());
      if (sinceDays > 0) params.set("since_days", String(sinceDays));
      const qs = params.toString();
      const r = await api.get(`/activity${qs ? `?${qs}` : ""}`);
      setItems(r.data);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actorId, action, sinceDays]);
  useEffect(() => { api.get("/activity/facets").then(r => setFacets(r.data)).catch(() => {}); }, []);

  const hasFilters = actorId || action || opportunityId || sinceDays > 0;
  const reset = () => { setActorId(""); setAction(""); setOpportunityId(""); setSinceDays(0); };

  const actionOptions = useMemo(() => facets.actions || [], [facets.actions]);

  return (
    <div className="p-8 stagger-in max-w-5xl" data-testid="activity-page">
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-2">// TEAM TIMELINE</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Activity</h1>
        <p className="text-slate-400 mt-2">Every action taken across your workspace, most recent first.</p>
      </div>

      <div className="bg-[#0D1424] border border-white/10 p-4 mb-6" data-testid="activity-filters">
        <div className="flex items-center gap-2 mono-label text-cyan-400 mb-3"><Filter className="w-3.5 h-3.5" />FILTERS</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <div className="mono-label text-slate-500 mb-1.5">TEAMMATE</div>
            <select value={actorId} onChange={(e) => setActorId(e.target.value)} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-actor">
              <option value="">All teammates</option>
              {(facets.actors || []).map(a => (
                <option key={a.actor_id} value={a.actor_id}>{a.name || a.email || a.actor_id}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="mono-label text-slate-500 mb-1.5">ACTION</div>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-action">
              <option value="">All actions</option>
              {actionOptions.map(a => (
                <option key={a} value={a}>{(LABELS[a] || a.replace(/_/g, " ")).toString().slice(0, 40)}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="mono-label text-slate-500 mb-1.5">OPPORTUNITY / NOTICE ID</div>
            <div className="flex gap-2">
              <input value={opportunityId} onChange={(e) => setOpportunityId(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="notice_id" className="flex-1 bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-opportunity" />
              <button onClick={load} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-3 mono-label" data-testid="apply-opportunity">GO</button>
            </div>
          </div>
          <div>
            <div className="mono-label text-slate-500 mb-1.5">TIME WINDOW</div>
            <select value={sinceDays} onChange={(e) => setSinceDays(parseInt(e.target.value, 10) || 0)} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-since">
              <option value={0}>All time</option>
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3 flex items-center gap-3">
            <button onClick={reset} className="mono-label text-slate-400 hover:text-red-400 flex items-center gap-1.5" data-testid="reset-filters"><X className="w-3.5 h-3.5" />RESET FILTERS</button>
            <span className="mono-label text-slate-500">· {items.length} MATCHING EVENT{items.length === 1 ? "" : "S"}</span>
          </div>
        )}
      </div>

      <div className="bg-[#0D1424] border border-white/10">
        {loading && (
          <div className="p-12 text-center text-slate-500 mono-label" data-testid="activity-loading">LOADING…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-12 text-center text-slate-500" data-testid="activity-empty">
            <ActivityIcon className="w-10 h-10 mx-auto mb-4 text-slate-600" />
            <div>{hasFilters ? "No events match these filters." : "No activity yet. Actions from your team will appear here."}</div>
          </div>
        )}
        {!loading && items.map(a => {
          const Icon = ICONS[a.action] || ActivityIcon;
          const label = LABELS[a.action] || a.action.replace(/_/g, " ");
          return (
            <div key={a.activity_id} className="p-5 border-b border-white/5 flex items-start gap-4" data-testid={`activity-${a.activity_id}`}>
              <div className="w-9 h-9 bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-cyan-400" strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-medium text-white">{a.actor_name || a.actor_email}</span>
                  <span className="text-slate-400"> {label} </span>
                  {a.target_id && a.target === "opportunity" ? (
                    <Link to={`/opportunities/${a.target_id}`} className="text-cyan-400 hover:text-cyan-300">{a.meta?.title || a.target_id.substring(0, 12)}</Link>
                  ) : a.target_id && a.target === "proposal" ? (
                    <Link to={`/proposals/${a.target_id}`} className="text-cyan-400 hover:text-cyan-300">{a.meta?.title || a.target_id.substring(0, 12)}</Link>
                  ) : (
                    <span className="text-cyan-400">{a.meta?.title || a.target_id || ""}</span>
                  )}
                  {a.meta?.stage && <span className="mono-label text-amber-400 ml-2">{String(a.meta.stage).replace(/_/g, " ")}</span>}
                </div>
                <div className="mono-label text-slate-500 mt-1">{timeAgo(a.created_at)} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
